"use client";

import type { ReactNode } from "react";
import { motion } from "framer-motion";
import { fadeLeft, fadeRight } from "@/lib/motion";
import { StarRating } from "@/components/ui/StarRating";
import { Button } from "@/components/ui/Button";
import { AccentColor } from "@/lib/colors";

const subheadingColorClass: Record<AccentColor, string> = {
  [AccentColor.Bubblegum]: "text-bubblegum-text",
  [AccentColor.Lavender]: "text-lavender-text",
  [AccentColor.Aegean]: "text-aegean-text",
  [AccentColor.Success]: "text-success-text",
  [AccentColor.Warning]: "text-warning-text",
  [AccentColor.Error]: "text-error-text"
};

type FooterProps =
  | { trustLine?: string; subtitle?: never }
  | { trustLine?: never; subtitle?: string };

export type ProductHeroProps = {
  color?: AccentColor;
  subheading: string;
  title: string;
  body: string;
  cta: {
    label: string;
    href: string;
    /** When true and href is an anchor, smoothly scrolls to that element on the page. */
    smoothScroll?: boolean;
  };
  media: ReactNode;
} & FooterProps;

export function ProductHero({
  color = AccentColor.Bubblegum,
  subheading,
  title,
  body,
  cta,
  trustLine,
  subtitle,
  media
}: ProductHeroProps) {
  const handleCtaClick = cta.smoothScroll
    ? (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        const targetId = cta.href.replace(/^#/, "");
        document
          .getElementById(targetId)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    : undefined;

  return (
    <section className="pt-6 lg:pt-32 pb-12 lg:pb-20 px-6">
      <div className="mx-auto max-w-7xl grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        <motion.div initial="hidden" animate="visible" variants={fadeLeft}>
          <p
            className={`${subheadingColorClass[color]} font-body text-sm font-bold uppercase tracking-widest mb-4`}
          >
            {subheading}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-text-primary leading-[1.05]">
            {title}
          </h1>
          <p className="mt-6 text-text-body max-w-lg">{body}</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Button href={cta.href} color={color} onClick={handleCtaClick}>
              {cta.label}
            </Button>
          </div>
          {trustLine && (
            <div className="mt-6 flex items-center gap-2.5 text-sm text-text-muted">
              <StarRating color={AccentColor.Warning} />
              {trustLine}
            </div>
          )}
          {subtitle && (
            <p className="mt-4 text-sm text-text-muted">{subtitle}</p>
          )}
        </motion.div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeRight}
          className="relative w-full min-w-0"
        >
          {media}
        </motion.div>
      </div>
    </section>
  );
}
